"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { formatOrdinal } from "@/helpers/format.helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { getDropPreviewImageUrl, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Link from "next/link";
interface LeadingCardProps {
  readonly drop: ExtendedDrop;
  readonly rank: number;
}
const getRankLabelClass = (rank: number) => {
  if (rank === 1) return "tw-text-[#E8D48A]";
  if (rank === 2) return "tw-text-[#DDDDDD]";
  if (rank === 3) return "tw-text-[#CD7F32]";
  return "tw-text-white/60";
};
export const LeadingCard = t0 => {
  const $ = _c(58);
  const {
    drop,
    rank
  } = t0;
  const {
    hasTouchScreen,
    isApp
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx600;
  const isHtml = media?.mime_type === "text/html";
  const shouldGate = hasTouchScreen && isHtml;
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t1;
  if ($[0] !== rank) {
    t1 = getRankLabelClass(rank);
    $[0] = rank;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rankLabelClass = t1;
  let t2;
  if ($[2] !== drop.metadata || $[3] !== isApp || $[4] !== isHtml) {
    t2 = isApp && isHtml ? getDropPreviewImageUrl(drop.metadata) : undefined;
    $[2] = drop.metadata;
    $[3] = isApp;
    $[4] = isHtml;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const htmlPreviewImageUrl = t2;
  let t3;
  bb0: {
    if (!media) {
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-iron-950"><span className="tw-text-sm tw-text-white/40">No image</span></div>;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t3 = t4;
      break bb0;
    }
    let t4;
    if ($[7] !== htmlPreviewImageUrl || $[8] !== media.mime_type || $[9] !== media.url || $[10] !== mediaImageScale || $[11] !== shouldGate) {
      t4 = <MediaDisplay media_mime_type={media.mime_type} media_url={media.url} disableMediaInteraction={true} imageScale={mediaImageScale} previewImageUrl={htmlPreviewImageUrl} requireInteractionToLoad={shouldGate} />;
      $[7] = htmlPreviewImageUrl;
      $[8] = media.mime_type;
      $[9] = media.url;
      $[10] = mediaImageScale;
      $[11] = shouldGate;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = t4;
  }
  const mediaContent = t3;
  const t4 = `tw-text-[11px] tw-font-semibold tw-leading-5 tw-tracking-wide ${rankLabelClass}`;
  let t5;
  if ($[13] !== rank) {
    t5 = rank === 1 ? "LEADING" : `${formatOrdinal(rank)} PLACE`;
    $[13] = rank;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== drop.rating) {
    t7 = formatNumberWithCommas(drop.rating);
    $[18] = drop.rating;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = <span className="tw-text-white/80">{t7}</span>;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="tw-text-white/50"> TDH</span>;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8) {
    t10 = <span className="tw-font-mono tw-text-xs">{t8}{t9}</span>;
    $[23] = t8;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t6) {
    t11 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/10 tw-bg-iron-900 tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2">{t6}{t10}</div>;
    $[25] = t10;
    $[26] = t6;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== mediaContent) {
    t12 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-iron-950"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{mediaContent}</div></div>;
    $[28] = mediaContent;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = media?.mime_type;
  let t14;
  if ($[30] !== drop.id || $[31] !== t13) {
    t14 = <MediaTypeBadge mimeType={t13} dropId={drop.id} size="sm" />;
    $[30] = drop.id;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== drop.id || $[34] !== drop.wave.id) {
    t15 = getWaveRoute({
      waveId: drop.wave.id,
      extraParams: {
        drop: drop.id
      },
      isDirectMessage: false,
      isApp: false
    });
    $[33] = drop.id;
    $[34] = drop.wave.id;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== title) {
    t16 = <Link href={t15} className="tw-m-0 tw-line-clamp-2 tw-text-base tw-font-semibold tw-leading-tight tw-text-iron-200 tw-no-underline tw-transition-colors group-hover:tw-text-white @lg:tw-line-clamp-1">{title}</Link>;
    $[36] = t15;
    $[37] = title;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t14 || $[40] !== t16) {
    t17 = <div className="tw-flex tw-items-center tw-gap-2">{t14}{t16}</div>;
    $[39] = t14;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = `/${author.handle ?? author.primary_address}`;
  const t19 = author.handle ?? "Artist";
  let t20;
  if ($[42] !== author.pfp || $[43] !== t19) {
    t20 = <ProfileAvatar pfpUrl={author.pfp} alt={t19} size={ProfileBadgeSize.SMALL} />;
    $[42] = author.pfp;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = author.handle ?? "Anonymous";
  let t22;
  if ($[45] !== t21) {
    t22 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t21}</span>;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t18 || $[48] !== t20 || $[49] !== t22) {
    t23 = <Link href={t18} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t20}{t22}</Link>;
    $[47] = t18;
    $[48] = t20;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t17 || $[52] !== t23) {
    t24 = <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-white/10 tw-px-3 tw-py-3 sm:tw-px-4">{t17}{t23}</div>;
    $[51] = t17;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t11 || $[55] !== t12 || $[56] !== t24) {
    t25 = <div className="tw-group tw-flex tw-flex-col tw-text-left tw-transition-all tw-duration-500"><div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-transition-colors group-hover:tw-border-white/10">{t11}{t12}{t24}</div></div>;
    $[54] = t11;
    $[55] = t12;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  return t25;
};
function _temp(m) {
  return m.data_key === "title";
}